// components/FeedbackSubmission.tsx
import React from "react";
import arrowLeft from "@assets/arrow_left.svg";
import checkCircle from "@assets/check_circle_blue.svg";
import Loader from "@shared/components/Loader";

interface Props {
  index: number;
  feedbackSubmittedFor: { [key: number]: string };
  feedbackState: { type: "like" | "dislike" | null; showInput: boolean };
  feedbackOption: string;
  feedbackText: string;
  feedbackUpdating: boolean;
  setFeedbackText: (text: string) => void;
  setFeedbackOption: (opt: string) => void;
  setFeedbackState: React.Dispatch<
    React.SetStateAction<{
      type: "like" | "dislike" | null;
      showInput: boolean;
    }>
  >;
  handleFeedbackSubmit: (index: number, feedback: string) => void;
  hideFeedbackTitle?: boolean;
  className?: string;
  feedbackSubmitted?: boolean;
}

const FeedbackSubmission: React.FC<Props> = ({
  index,
  feedbackSubmittedFor,
  feedbackState,
  feedbackOption,
  feedbackText,
  feedbackUpdating,
  setFeedbackText,
  setFeedbackOption,
  setFeedbackState,
  handleFeedbackSubmit,
  hideFeedbackTitle = false,
  className = "",
  feedbackSubmitted = false,
}) => {
  if (feedbackSubmitted) {
    return (
      <div
        className={`p-3 rounded-lg whitespace-pre-wrap break-words bg-[#F3F4F6] mt-3 w-full ${className}`}
      >
        <div className="text-xs 2xl:text-sm text-gray-800 flex items-center space-x-2">
          <img src={checkCircle} className="w-5 h-5" alt="" />
          <p>Thank you for your feedback!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-3 rounded-lg whitespace-pre-wrap break-words bg-[#F3F4F6] mt-3 w-full max-w-lg mx-auto ${className}`}
    >
      {feedbackSubmittedFor[index] ? (
        <div className="text-sm text-gray-800 flex items-center space-x-2">
          <img src={checkCircle} className="w-5 h-5" alt="" />
          <p>Thank you for your feedback!</p>
        </div>
      ) : (
        <>
          <div>
            {!hideFeedbackTitle && (
              <div className="flex space-x-2 items-center pb-3 ">
                {feedbackState.showInput && (
                  <button
                    onClick={() =>
                      setFeedbackState((prev) => ({
                        ...prev,
                        showInput: false,
                      }))
                    }
                    className="flex items-center text-sm text-[#2563eb]"
                  >
                    <img src={arrowLeft} className="w-4 h-4" alt="Back" />
                  </button>
                )}
                <p className="text-sm 2xl:text-base">
                  {feedbackState.type === "like"
                    ? "What did you like about this response?"
                    : "What didn't you like about this response?"}
                </p>
              </div>
            )}

            {!feedbackState.showInput ? (
              <div className="grid grid-cols-2 gap-2">
                {(feedbackState.type === "like"
                  ? [
                      "Accurate response",
                      "Good summary",
                      "Detailed response",
                      "Other",
                    ]
                  : [
                      "Inaccurate text",
                      "Inaccurate figures",
                      "Wrong content",
                      "Other",
                    ]
                ).map((label) =>
                  label === "Other" ? (
                    <button
                      key={label}
                      onClick={() =>
                        setFeedbackState((prev) => ({
                          ...prev,
                          showInput: true,
                        }))
                      }
                      className={`${
                        feedbackUpdating
                          ? "text-blue-400 border-blue-400 cursor-not-allowed"
                          : "text-blue-600 border-blue-600 hover:bg-blue-50"
                      } border-2 px-3 py-2 rounded text-xs 2xl:text-sm font-semibold`}
                      disabled={feedbackUpdating}
                    >
                      {label}
                    </button>
                  ) : (
                    <button
                      key={label}
                      onClick={() => {
                        handleFeedbackSubmit(index, label);
                        setFeedbackOption(label);
                      }}
                      className={`${
                        feedbackUpdating
                          ? "text-blue-400 border-blue-400 cursor-not-allowed"
                          : "text-blue-600 border-blue-600 hover:bg-blue-50"
                      } border-2 px-3 py-2 rounded text-xs 2xl:text-sm font-semibold`}
                      disabled={feedbackUpdating}
                    >
                      {feedbackOption === label && feedbackUpdating ? (
                        <Loader color="#5FA5FA" />
                      ) : (
                        <span>{label}</span>
                      )}
                    </button>
                  )
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  className="w-full border p-3 rounded text-xs 2xl:text-sm bg-transparent border-gray-600"
                  rows={3}
                  placeholder="Add more details..."
                  onChange={(e) => setFeedbackText(e.target.value)}
                  value={feedbackText}
                  disabled={feedbackUpdating}
                />
                <div className="w-full flex justify-end">
                  <button
                    onClick={() => handleFeedbackSubmit(index, feedbackText)}
                    className={`${
                      feedbackText.trim().length < 3 || feedbackUpdating
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    } text-white px-4 py-2 rounded text-xs 2xl:text-sm transition`}
                    disabled={
                      feedbackText.trim().length < 3 || feedbackUpdating
                    }
                  >
                    {feedbackUpdating ? (
                      <Loader color="#fff" />
                    ) : (
                      <span>Send</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackSubmission;
